/**
 * The sky.
 *
 * A flat background colour reads as cartoon no matter how good the ground is — the
 * horizon is a hard line and there is no sun in the sky. This is a gradient dome with a
 * real sun disc and a horizon haze, driven by the same day/night cycle as the lighting.
 */
import * as THREE from 'three'

const VERTEX = `
  varying vec3 vWorld;
  void main() {
    vWorld = (modelMatrix * vec4(position, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`

/**
 * Zenith → horizon gradient with a sun disc and its glow. The horizon band widens and
 * warms as the sun drops, which is what actually sells dawn and dusk.
 */
const FRAGMENT = `
  uniform vec3 uZenith;
  uniform vec3 uHorizon;
  uniform vec3 uSunColor;
  uniform vec3 uSunDirection;
  uniform float uSunIntensity;
  uniform vec3 uMoonDirection;
  uniform float uMoonIntensity;
  varying vec3 vWorld;

  // Cheap hash for the starfield.
  float hash(vec3 p) {
    p = fract(p * 0.3183099 + vec3(0.71, 0.113, 0.419));
    p *= 17.0;
    return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
  }

  void main() {
    vec3 dir = normalize(vWorld);
    float h = clamp(dir.y * 0.5 + 0.5, 0.0, 1.0);

    // Bias the blend toward the horizon so the band is tight, as it is in life.
    float t = pow(clamp(dir.y, 0.0, 1.0), 0.42);
    vec3 sky = mix(uHorizon, uZenith, t);

    // The sun itself, plus a wide soft glow around it.
    float d = max(dot(dir, normalize(uSunDirection)), 0.0);
    float disc = smoothstep(0.9975, 0.9992, d);
    float glow = pow(d, 220.0) * 0.5 + pow(d, 12.0) * 0.16;
    sky += uSunColor * (disc * 6.0 + glow) * uSunIntensity;

    // The moon, opposite the sun. Kate's night has to be playable, and a night sky with
    // nothing in it reads as a bug rather than as night.
    float m = max(dot(dir, normalize(uMoonDirection)), 0.0);
    // A defined disc with a tight halo. pow(m, 20) spread the glow across a third of
    // the sky and read as a dim smudge rather than a moon.
    float moonDisc = smoothstep(0.99965, 0.99985, m);
    float moonGlow = pow(m, 6000.0) * 0.9 + pow(m, 900.0) * 0.22;
    // A few craters, so it is a moon and not a white dot.
    float crater = 1.0 - 0.22 * step(0.99965, m) * hash(floor(dir * 900.0));
    sky += vec3(0.92, 0.95, 1.0) * (moonDisc * 7.0 * crater + moonGlow) * uMoonIntensity;

    // Stars, fading in as the sun goes down and thinning near the horizon haze.
    float starField = hash(floor(dir * 620.0));
    float star = smoothstep(0.9975, 1.0, starField) * uMoonIntensity * smoothstep(0.02, 0.35, dir.y);
    sky += vec3(0.9, 0.94, 1.0) * star * 0.9;

    // Haze thickening toward the horizon.
    sky = mix(sky, uHorizon, pow(1.0 - h, 6.0) * 0.5);
    gl_FragColor = vec4(sky, 1.0);
  }
`

export class Sky {
  readonly mesh: THREE.Mesh
  private readonly material: THREE.ShaderMaterial

  constructor() {
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uZenith: { value: new THREE.Color(0x3f7fc4) },
        uHorizon: { value: new THREE.Color(0xbcd4e4) },
        uSunColor: { value: new THREE.Color(0xfff0d0) },
        uSunDirection: { value: new THREE.Vector3(0, 1, 0) },
        uSunIntensity: { value: 1 },
        uMoonDirection: { value: new THREE.Vector3(0, -1, 0) },
        uMoonIntensity: { value: 0 },
      },
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      side: THREE.BackSide,
      depthWrite: false,
    })
    this.mesh = new THREE.Mesh(new THREE.SphereGeometry(600, 32, 20), this.material)
    this.mesh.frustumCulled = false
  }

  /** Drive from the day/night cycle: `daylight` 0..1 and the sun's world position. */
  update(daylight: number, sunPosition: THREE.Vector3, horizonCloseness: number): void {
    const u = this.material.uniforms
    // The moon sits opposite the sun, so it rises as the sun sets.
    ;(u.uMoonDirection!.value as THREE.Vector3).copy(sunPosition).multiplyScalar(-1).normalize()
    u.uMoonIntensity!.value = Math.max(0, 1 - daylight * 1.6)
    // Night is not black — Kate's night has to stay playable.
    ;(u.uZenith!.value as THREE.Color)
      // Night zenith is a deep blue, never black — you have to be able to see.
      .setHex(0x101f3d)
      .lerp(new THREE.Color(0x3f7fc4), daylight)
    ;(u.uHorizon!.value as THREE.Color)
      .setHex(0x1d2740)
      .lerp(new THREE.Color(0xbcd4e4), daylight)
      // Warm the horizon as the sun nears it — dawn and dusk.
      .lerp(new THREE.Color(0xff9a52), horizonCloseness * daylight * 0.75)
    ;(u.uSunColor!.value as THREE.Color)
      .setHex(0xfff0d0)
      .lerp(new THREE.Color(0xff8a3c), horizonCloseness)
    ;(u.uSunDirection!.value as THREE.Vector3).copy(sunPosition).normalize()
    u.uSunIntensity!.value = Math.max(0.05, daylight)
  }
}
